import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getGrondwaterproductie,
  createGrondwaterproductie,
  updateGrondwaterproductie,
  deleteGrondwaterproductie,
} from '../api/api';
import Modal from './Modal';

// 'IID-00042' → 'GPD-00042'
function maakGpdId(inrichtingId) {
  if (!inrichtingId) return '';
  return inrichtingId.replace(/^[A-Za-z]+/, 'GPD');
}

const KWARTAAL_KLEUREN = {
  1: '#a8d5f0', // Q1 babyblauw
  2: '#ffc8d3', // Q2 babyroze
  3: '#ff9500', // Q3 oranje
  4: '#5a9fd4', // Q4 donkerblauw
};

const HUIDIG_JAAR = new Date().getFullYear();
const VANDAAG = new Date().toISOString().slice(0, 10);

function fmtNum(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('nl-NL', { maximumFractionDigits: 0 });
}

export default function GrondwaterproductieDossier({ inrichtingId }) {
  const [metingen, setMetingen] = useState([]);
  const [error, setError] = useState(null);
  const [modus, setModus] = useState('grafiek');     // 'grafiek' | 'tabel'
  const [modalOpen, setModalOpen] = useState(false);
  const [bewerkMeting, setBewerkMeting] = useState(null);
  const [bezig, setBezig] = useState(false);

  const [form, setForm] = useState({
    jaar: HUIDIG_JAAR - 1,
    kwartaal: 1,
    onttrokken_m3: '',
    meet_nauwkeurigheid_pct: 95,
    aangeleverd_op: VANDAAG,
    opmerking: '',
  });

  const laad = useCallback(async () => {
    try {
      const r = await getGrondwaterproductie(inrichtingId);
      setMetingen(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, [inrichtingId]);

  useEffect(() => { laad(); }, [laad]);

  // Index: per jaar per kwartaal de meting
  const perJaar = useMemo(() => {
    const map = {};
    metingen.forEach(m => {
      if (!map[m.jaar]) map[m.jaar] = { Q1: null, Q2: null, Q3: null, Q4: null, totaal: 0 };
      map[m.jaar][`Q${m.kwartaal}`] = m;
      map[m.jaar].totaal += Number(m.onttrokken_m3) || 0;
    });
    return map;
  }, [metingen]);

  const jaren = useMemo(
    () => Object.keys(perJaar).sort((a, b) => Number(a) - Number(b)),
    [perJaar]
  );

  const opentModal = (meting = null) => {
    if (meting) {
      setBewerkMeting(meting);
      setForm({
        jaar: meting.jaar,
        kwartaal: meting.kwartaal,
        onttrokken_m3: meting.onttrokken_m3,
        meet_nauwkeurigheid_pct: meting.meet_nauwkeurigheid_pct ?? 95,
        aangeleverd_op: meting.aangeleverd_op,
        opmerking: meting.opmerking || '',
      });
    } else {
      setBewerkMeting(null);
      setForm({
        jaar: HUIDIG_JAAR - 1,
        kwartaal: 1,
        onttrokken_m3: '',
        meet_nauwkeurigheid_pct: 95,
        aangeleverd_op: VANDAAG,
        opmerking: '',
      });
    }
    setError(null);
    setModalOpen(true);
  };

  const opslaan = async () => {
    setBezig(true);
    setError(null);
    try {
      const payload = {
        jaar: Number(form.jaar),
        kwartaal: Number(form.kwartaal),
        onttrokken_m3: Number(form.onttrokken_m3),
        meet_nauwkeurigheid_pct: Number(form.meet_nauwkeurigheid_pct),
        aangeleverd_op: form.aangeleverd_op,
        opmerking: form.opmerking || null,
      };
      if (bewerkMeting) {
        await updateGrondwaterproductie(bewerkMeting.meting_id, payload);
      } else {
        await createGrondwaterproductie(inrichtingId, payload);
      }
      setModalOpen(false);
      await laad();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBezig(false);
    }
  };

  const verwijderen = async () => {
    if (!bewerkMeting) return;
    if (!window.confirm(`Meting ${bewerkMeting.jaar} Q${bewerkMeting.kwartaal} verwijderen?`)) return;
    setBezig(true);
    try {
      await deleteGrondwaterproductie(bewerkMeting.meting_id);
      setModalOpen(false);
      await laad();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBezig(false);
    }
  };

  const gpdId = maakGpdId(inrichtingId);

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Grondwaterproductiedossier <span className="mono">{gpdId}</span></h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="row" style={{ gap: 0, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <button
              onClick={() => setModus('grafiek')}
              className={modus === 'grafiek' ? 'btn-primary' : 'btn-ghost'}
              style={{ padding: '6px 12px', fontSize: 13, border: 'none', borderRadius: 0 }}
            >Grafiek</button>
            <button
              onClick={() => setModus('tabel')}
              className={modus === 'tabel' ? 'btn-primary' : 'btn-ghost'}
              style={{ padding: '6px 12px', fontSize: 13, border: 'none', borderRadius: 0 }}
            >Tabel</button>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => opentModal()}
            style={{ padding: '6px 12px', fontSize: 13 }}
          >+ Meting</button>
        </div>
      </div>

      {error && !modalOpen && <div className="error">{error}</div>}

      {metingen.length === 0 ? (
        <div className="empty">
          Nog geen metingen. Voeg de eerste kwartaalmeting toe via de knop hierboven.
        </div>
      ) : modus === 'grafiek' ? (
        <Grafiek perJaar={perJaar} jaren={jaren} onCelKlik={opentModal} />
      ) : (
        <Matrix perJaar={perJaar} jaren={jaren} onCelKlik={opentModal} />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={bewerkMeting ? `Meting ${bewerkMeting.jaar} Q${bewerkMeting.kwartaal} bewerken` : 'Nieuwe meting'}
        footer={
          <>
            {bewerkMeting && (
              <button className="btn btn-danger" onClick={verwijderen} disabled={bezig} style={{ marginRight: 'auto' }}>
                Verwijderen
              </button>
            )}
            <button className="btn btn-ghost" onClick={() => setModalOpen(false)}>Annuleer</button>
            <button className="btn btn-primary" onClick={opslaan} disabled={bezig}>
              {bezig ? 'Bezig…' : 'Opslaan'}
            </button>
          </>
        }
      >
        {error && <div className="error">{error}</div>}
        <div className="grid-2">
          <div className="field">
            <label className="field-label">Jaar</label>
            <input
              type="number" min="2000" max="2100"
              value={form.jaar}
              onChange={(e) => setForm({ ...form, jaar: e.target.value })}
            />
          </div>
          <div className="field">
            <label className="field-label">Kwartaal</label>
            <select
              value={form.kwartaal}
              onChange={(e) => setForm({ ...form, kwartaal: e.target.value })}
            >
              <option value={1}>Q1 (jan–mrt)</option>
              <option value={2}>Q2 (apr–jun)</option>
              <option value={3}>Q3 (jul–sep)</option>
              <option value={4}>Q4 (okt–dec)</option>
            </select>
          </div>
        </div>
        <div className="grid-2">
          <div className="field">
            <label className="field-label">Onttrokken (m³)</label>
            <input
              type="number" min="0" step="any"
              value={form.onttrokken_m3}
              onChange={(e) => setForm({ ...form, onttrokken_m3: e.target.value })}
              autoFocus
            />
          </div>
          <div className="field">
            <label className="field-label">Nauwkeurigheid (%)</label>
            <input
              type="number" min="0" max="100" step="0.1"
              value={form.meet_nauwkeurigheid_pct}
              onChange={(e) => setForm({ ...form, meet_nauwkeurigheid_pct: e.target.value })}
            />
          </div>
        </div>
        <div className="field">
          <label className="field-label">Aangeleverd op</label>
          <input
            type="date"
            value={form.aangeleverd_op}
            onChange={(e) => setForm({ ...form, aangeleverd_op: e.target.value })}
          />
        </div>
        <div className="field">
          <label className="field-label">Opmerking</label>
          <textarea
            rows="2"
            value={form.opmerking}
            onChange={(e) => setForm({ ...form, opmerking: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// Grafiek (gestapelde staaf per jaar)
// =====================================================

function Grafiek({ perJaar, jaren, onCelKlik }) {
  // Schaal o.b.v. grootste jaar-totaal
  const max = Math.max(1, ...jaren.map(j => perJaar[j].totaal));
  const W = 720, H = 320, padL = 60, padR = 20, padT = 20, padB = 50;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barW = Math.min(70, innerW / Math.max(jaren.length, 1) * 0.7);
  const stap = innerW / Math.max(jaren.length, 1);

  // Y-as: 5 ticks
  const tickWaarden = [0, 0.25, 0.5, 0.75, 1].map(f => f * max);

  return (
    <div style={{ overflow: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 480, height: 'auto' }}>
        {/* Y-as gridlijnen + labels */}
        {tickWaarden.map((tw, i) => {
          const y = padT + innerH - (tw / max) * innerH;
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e5e5e7" strokeWidth="1" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#6e6e73">
                {fmtNum(tw)}
              </text>
            </g>
          );
        })}

        {/* Y-as titel */}
        <text x={12} y={padT - 6} fontSize="11" fill="#6e6e73">m³</text>

        {/* Staven per jaar */}
        {jaren.map((jaar, idx) => {
          const data = perJaar[jaar];
          const xCenter = padL + stap * idx + stap / 2;
          let y = padT + innerH;
          return (
            <g key={jaar}>
              {[1, 2, 3, 4].map(q => {
                const m = data[`Q${q}`];
                if (!m) return null;
                const v = Number(m.onttrokken_m3) || 0;
                const h = (v / max) * innerH;
                y -= h;
                return (
                  <rect
                    key={q}
                    x={xCenter - barW / 2}
                    y={y}
                    width={barW}
                    height={h}
                    fill={KWARTAAL_KLEUREN[q]}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onCelKlik(m)}
                  >
                    <title>{`${jaar} Q${q}: ${fmtNum(v)} m³ (aangeleverd ${m.aangeleverd_op})`}</title>
                  </rect>
                );
              })}
              {/* Totaal-label boven de staaf */}
              <text x={xCenter} y={y - 6} textAnchor="middle" fontSize="11" fill="#3d3d42" fontWeight="500">
                {fmtNum(data.totaal)}
              </text>
              {/* X-as label (jaar) */}
              <text x={xCenter} y={H - padB + 18} textAnchor="middle" fontSize="13" fill="#3d3d42">
                {jaar}
              </text>
            </g>
          );
        })}

        {/* X-as lijn */}
        <line x1={padL} y1={padT + innerH} x2={W - padR} y2={padT + innerH} stroke="#3d3d42" strokeWidth="1" />
      </svg>

      {/* Legenda */}
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8, fontSize: 12, color: 'var(--text-soft)' }}>
        {[1, 2, 3, 4].map(q => (
          <span key={q} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              display: 'inline-block', width: 12, height: 12,
              background: KWARTAAL_KLEUREN[q], borderRadius: 2,
            }} />
            Q{q}
          </span>
        ))}
      </div>
    </div>
  );
}

// =====================================================
// Tabel (matrix)
// =====================================================

function Matrix({ perJaar, jaren, onCelKlik }) {
  const cellStyle = {
    cursor: 'pointer',
    textAlign: 'right',
  };
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Jaar</th>
            <th style={{ textAlign: 'right' }}>Q1 (m³)</th>
            <th style={{ textAlign: 'right' }}>Q2 (m³)</th>
            <th style={{ textAlign: 'right' }}>Q3 (m³)</th>
            <th style={{ textAlign: 'right' }}>Q4 (m³)</th>
            <th style={{ textAlign: 'right' }}>Totaal (m³)</th>
          </tr>
        </thead>
        <tbody>
          {jaren.map(jaar => {
            const d = perJaar[jaar];
            return (
              <tr key={jaar}>
                <td><strong>{jaar}</strong></td>
                {[1, 2, 3, 4].map(q => {
                  const m = d[`Q${q}`];
                  return (
                    <td
                      key={q}
                      style={cellStyle}
                      className={m ? 'clickable' : undefined}
                      onClick={m ? () => onCelKlik(m) : undefined}
                      title={m ? `Aangeleverd ${m.aangeleverd_op}${m.opmerking ? ` — ${m.opmerking}` : ''}` : ''}
                    >
                      {m ? fmtNum(m.onttrokken_m3) : '—'}
                    </td>
                  );
                })}
                <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmtNum(d.totaal)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
